/**
 * Shared trade document field definitions and enum values.
 *
 * Single source of truth for all trade field metadata used across:
 * - Trade Image Capture (TIC) admin mapping panel
 * - Trade Import config panel (KAN-259)
 * - Output Formats template editor
 *
 * Display labels use i18n keys — consumers call t(labelKey) to resolve.
 *
 * FX: Grouping1/Grouping2 are NOT included here — they are added dynamically
 * at runtime with client-specific labels from the groupings config.
 *
 * Commodities: Field list derived from KAN-248 data model (Sections 6.2–6.8).
 * Groupings are included in the constant since commodity groupings don't yet
 * have a dynamic config panel.
 */

// ========== Trade Document Fields ==========

export interface TradeFieldDef {
  field: string;
  labelKey: string; // i18n key, e.g. 'tradeFields.direction'
  type: 'string' | 'number' | 'date';
  calculated?: boolean; // true = derived during enrichment, not user-mappable
  required?: boolean;   // true = must be populated for import validation
}

export const TRADE_DOCUMENT_FIELDS: TradeFieldDef[] = [
  { field: 'tradeType', labelKey: 'tradeFields.tradeType', type: 'string' },
  { field: 'direction', labelKey: 'tradeFields.direction', type: 'string' },
  { field: 'amount', labelKey: 'tradeFields.amount', type: 'number' },
  { field: 'currency1', labelKey: 'tradeFields.currency1', type: 'string' },
  { field: 'currency2', labelKey: 'tradeFields.currency2', type: 'string' },
  { field: 'price', labelKey: 'tradeFields.price', type: 'number' },
  { field: 'spotPrice', labelKey: 'tradeFields.spotPrice', type: 'number' },
  { field: 'forwardPoints', labelKey: 'tradeFields.forwardPoints', type: 'number' },
  { field: 'contraAmount', labelKey: 'tradeFields.contraAmount', type: 'number' },
  { field: 'tradeDate', labelKey: 'tradeFields.tradeDate', type: 'date' },
  { field: 'valueDate', labelKey: 'tradeFields.valueDate', type: 'date' },
  { field: 'maturityDate', labelKey: 'tradeFields.maturityDate', type: 'date' },
  { field: 'paymentDate', labelKey: 'tradeFields.paymentDate', type: 'date' },
  { field: 'settlementType', labelKey: 'tradeFields.settlementType', type: 'string' },
  { field: 'settlementCurrency', labelKey: 'tradeFields.settlementCurrency', type: 'string' },
  { field: 'counterparty', labelKey: 'tradeFields.counterparty', type: 'string' },
  { field: 'ourParty', labelKey: 'tradeFields.ourParty', type: 'string' },
  { field: 'fixingReference', labelKey: 'tradeFields.fixingReference', type: 'string' },
  { field: 'spotFixingReference', labelKey: 'tradeFields.spotFixingReference', type: 'string' },
  { field: 'fixingOffset', labelKey: 'tradeFields.fixingOffset', type: 'string' },
  { field: 'hedgeObject', labelKey: 'tradeFields.hedgeObject', type: 'string' },
  { field: 'parentTradeNumber', labelKey: 'tradeFields.parentTradeNumber', type: 'string' },
  { field: 'mirrorId', labelKey: 'tradeFields.mirrorId', type: 'string' },
  { field: 'notes', labelKey: 'tradeFields.notes', type: 'string' },
  { field: 'isInternalTrade', labelKey: 'tradeFields.isInternalTrade', type: 'string' },
  { field: 'legacyReference1', labelKey: 'tradeFields.legacyReference1', type: 'string' },
  { field: 'legacyReference2', labelKey: 'tradeFields.legacyReference2', type: 'string' },
];

// ========== Trade Field Enum Values ==========

export interface TradeEnumValue {
  value: string;
  labelKey: string; // i18n key, e.g. 'tradeFields.enums.buy'
}

export const TRADE_FIELD_ENUMS: Record<string, TradeEnumValue[]> = {
  tradeType: [
    { value: 'SPOT', labelKey: 'tradeFields.enums.spot' },
    { value: 'FORWARD', labelKey: 'tradeFields.enums.forward' },
    { value: 'FORWARD_ASIATICO', labelKey: 'tradeFields.enums.forwardAsiatico' },
  ],
  direction: [
    { value: 'BUY', labelKey: 'tradeFields.enums.buy' },
    { value: 'SELL', labelKey: 'tradeFields.enums.sell' },
  ],
  settlementType: [
    { value: 'ENTREGA_FISICA', labelKey: 'tradeFields.enums.entregaFisica' },
    { value: 'COMPENSACION', labelKey: 'tradeFields.enums.compensacion' },
  ],
  fixingReference: [
    { value: 'USD Obs', labelKey: 'tradeFields.enums.usdObs' },
    { value: 'Bloomberg 11 AM', labelKey: 'tradeFields.enums.bloomberg11AM' },
    { value: 'UF', labelKey: 'tradeFields.enums.uf' },
  ],
  spotFixingReference: [
    { value: 'USD Obs', labelKey: 'tradeFields.enums.usdObs' },
    { value: 'Bloomberg 11 AM', labelKey: 'tradeFields.enums.bloomberg11AM' },
    { value: 'UF', labelKey: 'tradeFields.enums.uf' },
  ],
  isInternalTrade: [
    { value: 'TRUE', labelKey: 'tradeFields.enums.yes' },
    { value: 'FALSE', labelKey: 'tradeFields.enums.no' },
  ],
  eventCode: [
    { value: 'New Trade',         labelKey: 'customReports.eventTypes.newTrade' },
    { value: 'Expired',           labelKey: 'customReports.eventTypes.expired' },
    { value: 'Roll-Over',         labelKey: 'customReports.eventTypes.rollOver' },
    { value: 'Modified',          labelKey: 'customReports.eventTypes.modified' },
    { value: 'Cancelled',         labelKey: 'customReports.eventTypes.cancelled' },
    { value: 'Early Termination', labelKey: 'customReports.eventTypes.earlyTermination' },
  ],
};

// ========== KAN-259: Commodity Document Fields ==========

export const COMMODITY_DOCUMENT_FIELDS: TradeFieldDef[] = [
  // Parties
  { field: 'OurParty', labelKey: 'commodities.fields.ourParty', type: 'string', required: true },

  // Product identification
  { field: 'ProductType', labelKey: 'commodities.fields.productType', type: 'string', required: true },
  { field: 'OptionType', labelKey: 'commodities.fields.optionType', type: 'string' },
  { field: 'ExchangeContractCode', labelKey: 'commodities.fields.exchangeContractCode', type: 'string' },
  { field: 'ContractMonth', labelKey: 'commodities.fields.contractMonth', type: 'string' },
  { field: 'FixingReference', labelKey: 'commodities.fields.fixingReference', type: 'string' },
  { field: 'UnderlyingCode', labelKey: 'commodities.fields.underlyingCode', type: 'string', required: true },
  { field: 'Direction', labelKey: 'commodities.fields.direction', type: 'string', required: true },
  { field: 'CounterpartyName', labelKey: 'commodities.fields.counterpartyName', type: 'string', required: true },

  // Trade economics
  { field: 'Lots', labelKey: 'commodities.fields.lots', type: 'number', required: true },
  { field: 'Price', labelKey: 'commodities.fields.price', type: 'number', required: true },
  { field: 'SettlementPrice', labelKey: 'commodities.fields.settlementPrice', type: 'number' },
  { field: 'Premium', labelKey: 'commodities.fields.premium', type: 'number' },
  { field: 'ExecutionType', labelKey: 'commodities.fields.executionType', type: 'string' },

  // Dates
  { field: 'TradeDate', labelKey: 'commodities.fields.tradeDate', type: 'date', required: true },
  { field: 'ValueDate', labelKey: 'commodities.fields.valueDate', type: 'date' },
  { field: 'MaturityDate', labelKey: 'commodities.fields.maturityDate', type: 'date', required: true },
  { field: 'PaymentDate', labelKey: 'commodities.fields.paymentDate', type: 'date' },

  // Venue
  { field: 'VenueCode', labelKey: 'commodities.fields.venueCode', type: 'string' },

  // Classification
  { field: 'Grouping1', labelKey: 'commodities.fields.grouping1', type: 'string' },
  { field: 'Grouping2', labelKey: 'commodities.fields.grouping2', type: 'string' },
  { field: 'Grouping3', labelKey: 'commodities.fields.grouping3', type: 'string' },
  { field: 'HedgeObject', labelKey: 'commodities.fields.hedgeObject', type: 'string' },
  { field: 'Notes', labelKey: 'commodities.fields.notes', type: 'string' },

  // Source/legacy references
  { field: 'LegacyReference1', labelKey: 'commodities.fields.legacyReference1', type: 'string' },
  { field: 'LegacyReference2', labelKey: 'commodities.fields.legacyReference2', type: 'string' },
  { field: 'SourceSystem', labelKey: 'commodities.fields.sourceSystem', type: 'string' },

  // Internal allocation intent (consumed by import engine → written as pendingAllocations array)
  { field: 'InternalCounterparty', labelKey: 'commodities.fields.internalCounterparty', type: 'string' },
  { field: 'AllocationPercentage', labelKey: 'commodities.fields.allocationPercentage', type: 'number' },

  // Calculated fields (derived during enrichment, not user-mappable)
  { field: 'Currency', labelKey: 'commodities.fields.currency', type: 'string', calculated: true },
  { field: 'Quantity', labelKey: 'commodities.fields.quantity', type: 'number', calculated: true },
  { field: 'UnitOfMeasure', labelKey: 'commodities.fields.unitOfMeasure', type: 'string', calculated: true },
  { field: 'SettlementAmount', labelKey: 'commodities.fields.settlementAmount', type: 'number', calculated: true },
  { field: 'SettlementCurrency', labelKey: 'commodities.fields.settlementCurrency', type: 'string', calculated: true },
];

// ========== KAN-259: Commodity Field Enum Values ==========

export const COMMODITY_FIELD_ENUMS: Record<string, TradeEnumValue[]> = {
  ProductType: [
    { value: 'Swap', labelKey: 'commodities.enums.swap' },
    { value: 'Option', labelKey: 'commodities.enums.option' },
    { value: 'Future', labelKey: 'commodities.enums.future' },
  ],
  Direction: [
    { value: 'Buy', labelKey: 'commodities.enums.buy' },
    { value: 'Sell', labelKey: 'commodities.enums.sell' },
  ],
  OptionType: [
    { value: 'Put', labelKey: 'commodities.enums.put' },
    { value: 'Call', labelKey: 'commodities.enums.call' },
  ],
  ExecutionType: [
    { value: 'Exchange', labelKey: 'commodities.enums.exchange' },
    { value: 'OTC', labelKey: 'commodities.enums.otc' },
  ],
  // Shared enums (same values as FX)
  isInternalTrade: [
    { value: 'TRUE', labelKey: 'tradeFields.enums.yes' },
    { value: 'FALSE', labelKey: 'tradeFields.enums.no' },
  ],
  eventCode: [
    { value: 'New Trade',         labelKey: 'customReports.eventTypes.newTrade' },
    { value: 'Expired',           labelKey: 'customReports.eventTypes.expired' },
  ],
};

// ========== KAN-259: Module Registry Functions ==========

export function getFieldsForModule(module: string): TradeFieldDef[] {
  switch (module) {
    case 'Commodities': return COMMODITY_DOCUMENT_FIELDS;
    case 'FX': return TRADE_DOCUMENT_FIELDS;
    default: return [];
  }
}

export function getEnumsForModule(module: string): Record<string, TradeEnumValue[]> {
  switch (module) {
    case 'Commodities': return COMMODITY_FIELD_ENUMS;
    case 'FX': return TRADE_FIELD_ENUMS;
    default: return {};
  }
}
